import { Stack, Typography, TextField, Checkbox, Autocomplete, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { HTMLAttributes, Key } from 'react';
import { getIn } from 'formik';
import TextFieldLabel from '@shared/components/inputs/TextFieldLabel/TextFieldLabel';
import FormErrorMessage from '@shared/components/feedback/FormErrorMessage/FormErrorMessage';
import { SERVICE_OPTIONS } from '@constants/leadCaptureConstants';
import type { LeadCaptureSectionProps } from '@components/LeadCaptureForm/LeadCaptureForm.types';

export const ServiceOfInterestField = ({ formik }: LeadCaptureSectionProps) => {
  const { values, touched, errors, setFieldValue, setFieldTouched } = formik;
  const isTouched = Boolean(getIn(touched, 'businessFields.serviceOfInterest'));
  const errorMessage = getIn(errors, 'businessFields.serviceOfInterest') as string | undefined;
  const hasError = isTouched && !!errorMessage;
  const hasSelection = values.businessFields.serviceOfInterest.length > 0;
  const placeholder = hasSelection ? '' : 'Select a Service of Interest';

  return (
    <Stack gap="10px">
      <TextFieldLabel label="Service of Interest" />
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={SERVICE_OPTIONS}
        value={values.businessFields.serviceOfInterest}
        onChange={(_event, next) => setFieldValue('businessFields.serviceOfInterest', next)}
        onBlur={() => setFieldTouched('businessFields.serviceOfInterest', true)}
        isOptionEqualToValue={(option, val) => option.value === val.value}
        renderOption={(props, option, { selected }) => {
          const { key, ...liProps } = props as HTMLAttributes<HTMLLIElement> & { key?: Key };
          return (
            <li key={key} {...liProps}>
              <Stack direction="row" alignItems="center" gap="10px">
                <Checkbox checked={selected} />
                <Typography variant="option.label">{option.label}</Typography>
              </Stack>
            </li>
          );
        }}
        renderTags={(selectedOptions, getTagProps) =>
          selectedOptions.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip key={key} {...tagProps} label={option.label} variant="selectedTag" deleteIcon={<CloseIcon />} />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            error={hasError}
            helperText={hasError ? errorMessage : ''}
            FormHelperTextProps={{ component: FormErrorMessage }}
          />
        )}
      />
    </Stack>
  );
};
